import React, {
  HTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';

import { LARGE_LAPTOP_AND_DESKTOP } from 'settings/mediaQuery';

import styles from './StickyWrapper.module.scss';

type PropsType = HTMLAttributes<HTMLDivElement> & {
  children: (isSticky: boolean) => React.ReactNode;
};

export default function StickyWrapper(props: PropsType): JSX.Element {
  const { children, className, ...rest } = props;
  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef<HTMLDivElement | null>(null);
  const headerHeightRef = useRef(0);
  const isDesktop = useMediaQuery({ query: LARGE_LAPTOP_AND_DESKTOP });

  const scrollHandler = useCallback(() => {
    const stickyEl = stickyRef.current;

    if (stickyEl) {
      setIsSticky(
        stickyEl.getBoundingClientRect().top <= headerHeightRef.current
      );
    }
  }, []);

  useEffect(() => {
    if (!isDesktop) return;

    const header = document.getElementById('headerContainerLayout');
    const stickyEl = stickyRef.current;

    if (!header || !stickyEl) return;

    const resizeObserver = new ResizeObserver(([entry]) => {
      const headerHeight = entry.contentRect.height;

      stickyEl.style.top = `${headerHeight}px`;
      headerHeightRef.current = headerHeight;
    });

    resizeObserver.observe(header);

    window.addEventListener('scroll', scrollHandler);
    scrollHandler();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [isDesktop]);

  return (
    <div
      ref={stickyRef}
      className={classNames(
        styles.container,
        className,
        isSticky && styles.sticky
      )}
      {...rest}
    >
      {children(isSticky)}
    </div>
  );
}
